import { Form, Select, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import Button from '../../../components/Button/Button';
import FormItem, { FileFormItem } from '../../../components/FormItem/FormItem';
import Input from '../../../components/Input/Input';
import { createErrorNotification, createSuccessNotification } from '../../../service/notification';
import { DEFAULT_ERROR_MESSAGE } from '../../../models/common/async-result';
import { getUser, updateUser, validateNick } from '../../../service/user';
import { User, UserRole } from '../../../models/user';
import { useUserStore } from '../../../store/useUser';
import { editUsersGenres, getAllGenres, getUserGenres } from '../../../service/genres';

import { useQuery } from 'react-query';
import FileInput from '../../../components/FileInput/FileInput';
import CustomDatePicker from '../../../components/DatePicker/DatePicker';

import styles from './EditUser.module.scss';
import moment from 'moment';
import { LiteraryGenre } from '../../../models/literaryGenre';
import { uploadUserImage } from '../../../service/files';

import ImageInput from '../../../components/ImageInput/ImageInput';
import useHeader from '../../../hooks/useHeader';

const { Option } = Select;

const EditUser = () => {
  const [form] = Form.useForm();

  const [role, setRole] = useState<UserRole>('primary');
  const [isLoading, setIsLoading] = useState(false);

  const userId = useUserStore(state => state.id)

  const {data: user, isFetching: isFetchingUser} = useQuery(
    ['user'],
    () => getUser(userId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const { data: userGenres, isFetching: isFetchingUserGenres} = useQuery(
    ['user-genres'],
    () => getUserGenres(userId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const {data:genres, isFetching: isFetchingAllGenres} = useQuery(
    ['genres'],
    () => getAllGenres(),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  useHeader({ header: 'Suas Configurações' });

  useEffect(() => {
    if (!user) return;

    form.setFields([
      { name: 'fullname', value: user.fullname },
      { name: 'email', value: user.email },
      { name: 'nick', value: user.nick },
      { name: 'city', value: user.city },
      { name: 'birthdate', value: user.birthdate ? moment(user.birthdate) : '' },
      { name: 'description', value: user.description },
    ])
  }, [form, user])

  useEffect(() => {
    if (!userGenres?.length) return;

    form.setFields([
      { name: 'genres', value: userGenres },
    ])
  }, [form, userGenres])

  const onFinish = async (payload: User & { genres: Array<LiteraryGenre | number>, file: File }) =>{
    if(isLoading) return;
    
    setIsLoading(true);
    
    const format = 'YYYY-MM-DD 00:00'
    
    if (payload.birthdate) {
      payload.birthdate = moment(payload.birthdate).format(format)
    }

    const { error, success } = await updateUser(userId, payload);
    
    if (error) {
      setIsLoading(false);
      return createErrorNotification('Ops, algo deu errado na atualização. Tente novamente mais tarde.');
    }

    let newGenres: number[] = [];

    if (payload.genres) {
      newGenres = payload.genres.map(genre => {
        if(typeof genre === 'number') return genre

        return genre?.id
      })

      await editUsersGenres(userId, newGenres)
    }

    if (payload.file) {
      await uploadUserImage(userId, payload.file)
    }
    
    setIsLoading(false);

    createSuccessNotification("Usuário atualizado.")
  }

  function onSelectImage(file: File) {
    form.setFields([
      {name: 'file', value: file},
    ]);

    form.validateFields(['file']);
  }

  const loadingData = (isFetchingAllGenres || isFetchingUserGenres || isFetchingUser)

  return(
    <div className={styles.container}>
      <div className={styles.maskContainer}></div>
        <div className={styles.createContainer}>
          <div className={styles.createForm}>
            <Spin spinning={loadingData}>
            <Form 
              layout="vertical" 
              onFinish={onFinish} 
              hideRequiredMark 
              id="formLogin" 
              form={form}
              className={styles.FormContainer}
            >
              <h2 className={styles.title}>Editar usuário</h2>
              <FormItem
                label="Nome"
                name="fullname"
                rules={[
                  {
                    required: true, 
                    message: 'Por favor, insira seu nome!'
                  }
                ]}>
                  <Input style={styles.input}/>
                </FormItem>
                <FormItem
                  label="Email"
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: 'Por favor, insira seu email!'
                    }
                  ]}>
                  <Input disabled style={styles.input} type="email"/>
                </FormItem>
                <FormItem
                  label="Nome de usuário"
                  name="nick"
                  rules={[
                    {
                      required: true,
                    },
                    {
                      validateTrigger: 'onBlur',
                      validator: async (_, value) => {
                        if (!value) return

                        const testNick = /^[A-Za-z0-9_]+$/.test(value);

                        if (!testNick) {
                          return Promise.reject(new Error('Por favor, insira um nick válido! Apenas letras, números e _'));
                        }

                        const { error } = await validateNick(userId, value)

                        if (error) return Promise.reject(new Error(error));

                        return Promise.resolve();
                      }
                    },
                  ]}
                    >
                      <Input />
                </FormItem>
                <FormItem
                  label="Mini Biografia"
                  name="description">
                  <Input style={styles.input} />
                </FormItem>
                  <FormItem
                    label="Data de Nascimento"
                    name="birthdate">
                    <CustomDatePicker 
                      onChange={(value, string) => {
                        return string
                      }}
                    />
                  </FormItem>
                  <FormItem
                    label="Gêneros Literários Favoritos"
                    name="genres">
                    <Select
                      mode="multiple"
                      >
                      { 
                        genres?.map(genre => {
                          return <Option key={genre.id} value={genre.id}>{genre.content}</Option>
                        })
                      }
                    </Select>
                  </FormItem>
                  <FormItem
                    label="Cidade"
                    name="city"
                  >
                    <Input />
                  </FormItem>
                  <FileFormItem
                    name="file"
                    label="Foto de perfil"
                    >
                    <ImageInput
                        accept='image/*' 
                        title="Arraste e solte sua foto de perfil"
                        onChangeFile={onSelectImage}
                    />
                  </FileFormItem>
                <Button disabled={isLoading || loadingData} loading={isLoading} submit style={styles.button}>Editar usuário</Button>
              </Form>
            </Spin>
          </div>
      </div>
    </div>
  )
}

export default EditUser;